// Archivo para centralizar la URL base del backend y funciones de fetch
// Version 2.0 - Soporte para múltiples anteojos por cliente

import { authService } from './auth';

export const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const API_V2_URL = `${BACKEND_URL}/api/v2`;

function headersToObject(headers) {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  return { ...headers };
}

function getAuthHeaders() {
  const token = authService.obtenerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildRequestHeaders(extraHeaders = {}) {
  const headers = new Headers();
  const authHeaders = getAuthHeaders();

  Object.entries(authHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });

  Object.entries(headersToObject(extraHeaders)).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      headers.set(key, value);
    }
  });

  return headers;
}

async function parseErrorDetail(response) {
  try {
    const error = await response.json();
    return error?.detail || null;
  } catch {
    return null;
  }
}

async function apiFetch(url, options = {}, intentos = 0) {
  const token = authService.obtenerToken();
  const headers = buildRequestHeaders(options.headers);

  if (!token) {
    console.warn('[auth] No se encontró access_token al preparar request', {
      url,
      origin: globalThis.location?.origin || 'unknown',
    });
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    const detail = await parseErrorDetail(response.clone());
    console.warn('[auth] Request no autorizada', {
      url,
      hasAccessToken: Boolean(token),
      hasRefreshToken: Boolean(authService.obtenerRefreshToken()),
      detail,
    });
  }

  // Reintento único con refresh token si el access token expiró.
  if (response.status === 401 && intentos < 1) {
    if (authService.obtenerRefreshToken()) {
      try {
        await authService.renovarToken();
        return apiFetch(url, options, intentos + 1);
      } catch {
        authService.logout();
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        throw new Error('Sesión expirada. Iniciá sesión nuevamente.');
      }
    } else {
      // No hay refresh token: la sesión ya no es válida.
      authService.logout();
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
      throw new Error('Sesión expirada. Iniciá sesión nuevamente.');
    }
  }

  // Si la óptica fue desactivada, cerramos sesión para evitar UI inconsistente.
  if (response.status === 403) {
    const detail = await parseErrorDetail(response);
    if (typeof detail === 'string' && detail.toLowerCase().includes('óptica está desactivada')) {
      authService.logout();
      throw new Error(detail);
    }
  }

  return response;
}

// =============== FUNCIONES DE AUTENTICACIÓN ===============

// Registrar nuevo usuario
export async function registrarUsuario(usuarioData) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/registro`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(usuarioData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al registrar usuario");
  }
  return await response.json();
}

// =============== FUNCIONES PARA ANTEOJOS ===============

// Editar anteojo existente
export async function editarAnteojo(anteojoId, anteojoData) {
  const response = await apiFetch(`${API_V2_URL}/anteojos/${anteojoId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(anteojoData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al editar anteojo");
  }
  return await response.json();
}

// Obtener todos los anteojos (general)
export async function obtenerTodosAnteojos() {
  const response = await apiFetch(`${API_V2_URL}/anteojos/`);
  if (!response.ok) {
    throw new Error('Error al obtener todos los anteojos');
  }
  return await response.json();
}

// =============== FUNCIONES PARA ESTADÍSTICAS ===============

export async function obtenerEstadisticas({ mes, anio } = {}) {
  const params = new URLSearchParams();
  if (mes != null) params.set('mes', mes);
  if (anio != null) params.set('anio', anio);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiFetch(`${API_V2_URL}/estadisticas/${query}`);
  if (!response.ok) {
    throw new Error("Error al obtener estadísticas");
  }
  return await response.json();
}

// =============== FUNCIONES PARA CLIENTES V2 ===============

// Crear nuevo cliente (solo datos personales)
export async function crearCliente(clienteData) {
  // Normalizar nombre, apellido y teléfono antes de enviar
  const normalizarNombre = (valor) => {
    if (!valor) return "";
    return valor
      .trim()
      .replace(/\s+/g, ' ');
  };
  
  const normalizarTelefono = (valor) => {
    if (!valor) return "";
    return valor
      .replace(/[\s\-\(\)\.]+/g, '')
      .trim();
  };
  
  const clienteDataNormalizado = {
    ...clienteData,
    nombre: normalizarNombre(clienteData.nombre),
    apellido: normalizarNombre(clienteData.apellido),
    telefono: normalizarTelefono(clienteData.telefono)
  };
  
  const response = await apiFetch(`${API_V2_URL}/clientes/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(clienteDataNormalizado),
  });
  console.log("Cliente creado (normalizado):", clienteDataNormalizado);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al crear cliente");
  }
  return await response.json();
}

// Obtener todos los clientes con conteo de anteojos
export async function obtenerClientes() {
  const response = await apiFetch(`${API_V2_URL}/clientes/`,{
    method: "GET",
    headers: { "Content-Type": "application/json" },
  }

  );
  if (!response.ok) {
    throw new Error("Error al obtener clientes");
  }
  return await response.json();
}

// Obtener cliente específico por ID
export async function obtenerClientePorId(clienteId) {
  const response = await apiFetch(`${API_V2_URL}/clientes/${clienteId}`);
  if (!response.ok) {
    throw new Error("Error al obtener cliente");
  }
  return await response.json();
}

// Obtener cliente con todos sus anteojos
export async function obtenerClienteCompleto(clienteId) {
  const response = await apiFetch(`${API_V2_URL}/clientes/${clienteId}/completo`);
  if (!response.ok) {
    throw new Error("Error al obtener cliente completo");
  }
  return await response.json();
}

// Editar datos básicos del cliente
export async function editarCliente(clienteId, datosCliente) {
  // Normalizar nombre, apellido y teléfono antes de enviar
  const normalizarNombre = (valor) => {
    if (!valor) return "";
    return valor
      .trim()
      .replace(/\s+/g, ' ');
  };
  
  const normalizarTelefono = (valor) => {
    if (!valor) return "";
    return valor
      .replace(/[\s\-\(\)\.]+/g, '')
      .trim();
  };
  
  const datosClienteNormalizado = {
    ...datosCliente,
    ...(datosCliente.nombre && { nombre: normalizarNombre(datosCliente.nombre) }),
    ...(datosCliente.apellido && { apellido: normalizarNombre(datosCliente.apellido) }),
    ...(datosCliente.telefono && { telefono: normalizarTelefono(datosCliente.telefono) })
  };
  
  const response = await apiFetch(`${API_V2_URL}/clientes/${clienteId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(datosClienteNormalizado),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al editar cliente");
  }
  return await response.json();
}

// Eliminar cliente y sus anteojos
export async function eliminarCliente(clienteId) {
  const response = await apiFetch(`${API_V2_URL}/clientes/${clienteId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar cliente");
  }
  return await response.json();
}

// Buscar clientes por nombre
export async function buscarClientesPorNombre(nombre) {
  const response = await apiFetch(`${API_V2_URL}/clientes/buscar/nombre?nombre=${encodeURIComponent(nombre)}`);
  if (!response.ok) {
    throw new Error("Error al buscar clientes por nombre");
  }
  return await response.json();
}

// Verificar si existe cliente duplicado (versión mejorada)
export async function verificarClienteDuplicado(nombres, apellidos, telefonos, busquedaExacta = false) {
  // Convertir a arrays si son strings
  const nombresArray = Array.isArray(nombres) ? nombres : [nombres];
  const apellidosArray = Array.isArray(apellidos) ? apellidos : [apellidos];
  const telefonosArray = Array.isArray(telefonos) ? telefonos : [telefonos];
  
  // Función auxiliar para normalizar nombre/apellido
  const normalizarNombre = (valor) => {
    if (!valor) return "";
    return valor
      .trim()
      .replace(/\s+/g, ' '); // Reemplazar múltiples espacios por uno
  };
  
  // Función auxiliar para extraer últimos 8 dígitos del teléfono
  // Ignora: 0 inicial, 54 (country code), 9 (formato celular), etc.
  const extraerUltimos8Digitos = (valor) => {
    if (!valor) return "";
    
    let telefono = valor
      .replace(/[\s\-\(\)\.+]/g, '') // Remover espacios, guiones, paréntesis, puntos, +
      .trim();
    
    // Remover 0 inicial
    if (telefono.startsWith('0')) {
      telefono = telefono.substring(1);
    }
    
    // Remover 54 (country code Argentina)
    if (telefono.startsWith('54')) {
      telefono = telefono.substring(2);
    }
    
    // Remover 9 que a veces viene en celulares argentinos
    if (telefono.startsWith('9')) {
      telefono = telefono.substring(1);
    }
    
    // Extraer solo dígitos
    telefono = telefono.replace(/\D/g, '');
    
    // Retornar últimos 8 dígitos
    return telefono.length >= 8 ? telefono.slice(-8) : telefono;
  };
  
  // Validar que no estén vacíos
  if (!nombresArray.some(n => n?.trim()) || !apellidosArray.some(a => a?.trim()) || !telefonosArray.some(t => t?.trim())) {
    throw new Error("Nombre, apellido y teléfono son requeridos para verificar duplicados");
  }

  const payload = {
    nombres: nombresArray.map(n => normalizarNombre(n)).filter(Boolean),
    apellidos: apellidosArray.map(a => normalizarNombre(a)).filter(Boolean),
    telefonos: telefonosArray.map(t => extraerUltimos8Digitos(t)).filter(Boolean),
    busqueda_exacta: busquedaExacta
  };
  
  try {
    const response = await apiFetch(`${API_V2_URL}/clientes/verificar-duplicado`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      let errorMessage = "Error al verificar cliente duplicado";
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch (parseError) {
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }
      
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error("No se puede conectar con el servidor. Verifique que esté ejecutándose.");
    }
    throw error;
  }
}

// Versión simple para compatibilidad hacia atrás
export async function verificarClienteDuplicadoSimple(nombre, apellido, telefono) {
  return await verificarClienteDuplicado([nombre], [apellido], [telefono], true);
}

// Función avanzada con múltiples nombres y apellidos
export async function verificarClienteDuplicadoAvanzado(nombre, apellido, telefono, busquedaExacta = true) {
  // Función que mantiene la interfaz simple pero usa la nueva API
  const resultado = await verificarClienteDuplicado([nombre], [apellido], [telefono], busquedaExacta);
  
  // Adaptar el resultado al formato esperado por el ClienteForm
  if (resultado.clientes_encontrados && resultado.clientes_encontrados.length > 0) {
    return {
      duplicado: true,
      cliente_existente: resultado.clientes_encontrados[0], // Tomar el primer resultado
      total_encontrados: resultado.total_encontrados,
      todos_los_clientes: resultado.clientes_encontrados
    };
  }
  
  return {
    duplicado: false,
    cliente_existente: null,
    total_encontrados: 0,
    todos_los_clientes: []
  };
}

// Flujo completo: verifica duplicados y crea anteojo
// Retorna opciones si hay duplicado, o crea directamente si no
export async function verificarYCrearAnteojo(datosCliente, anteojoData, clienteIdExistente = null, confirmarCrearNuevo = false) {
  const payload = {
    cliente_id: clienteIdExistente || null,
    datos_cliente: datosCliente,
    anteojo: anteojoData,
    confirmar_crear_nuevo: confirmarCrearNuevo
  };

  const response = await fetch(`${API_V2_URL}/clientes/verificar-y-crear`, {
    method: 'POST',
    headers: buildRequestHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    let errorMessage = `Error ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorData.message || errorMessage;
    } catch (parseError) {
      errorMessage = `Error ${response.status}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return await response.json();
}

// Agregar anteojo a un cliente
export async function agregarAnteojo(clienteId, anteojoData) {
  const response = await apiFetch(`${API_V2_URL}/clientes/${clienteId}/anteojos/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(anteojoData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al agregar anteojo");
  }
  return await response.json();
}

// Obtener anteojos de un cliente
export async function obtenerAnteojosCliente(clienteId) {
  const response = await apiFetch(`${API_V2_URL}/clientes/${clienteId}/anteojos/`);
  if (!response.ok) {
    throw new Error("Error al obtener anteojos del cliente");
  }
  return await response.json();
}

// Buscar anteojo por número de sobre
export async function buscarAnteojosPorNroSobre(nroSobre) {
  const response = await apiFetch(`${API_V2_URL}/anteojos/buscar/sobre?nro_sobre=${encodeURIComponent(nroSobre)}`);
  if (!response.ok) {
    throw new Error("Error al buscar anteojo por número de sobre");
  }
  return await response.json();
}

// Actualizar estado de anteojo
export async function actualizarEstadoAnteojo(anteojoId, nuevoEstado) {
  const response = await apiFetch(`${API_V2_URL}/anteojos/${anteojoId}/estado`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nuevo_estado: nuevoEstado }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al actualizar estado del anteojo");
  }
  return await response.json();
}

// Obtener último número de sobre
export async function obtenerUltimoNroSobre() {
  const response = await apiFetch(`${API_V2_URL}/anteojos/ultimo-sobre`);
  if (!response.ok) {
    throw new Error("Error al obtener último número de sobre");
  }
  return await response.json();
}

// =============== FUNCIONES LEGACY (API V1) ===============
// Mantenemos compatibilidad con el código existente

// Guardar nuevo cliente (formato antiguo)
export async function guardarCliente(clienteData) {
  const response = await apiFetch(`${BACKEND_URL}/clientes/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(clienteData),
  });
  if (!response.ok) {
    throw new Error("Error al guardar cliente");
  }
  return await response.json();
}

// Buscar clientes por apellido (API v1)
export async function buscarClientesPorApellido(apellido) {
  const response = await apiFetch(`${BACKEND_URL}/clientes/apellido/?apellido=${encodeURIComponent(apellido)}`);
  if (!response.ok) {
    throw new Error("Error al buscar clientes por apellido");
  }
  return await response.json();
}

// Buscar cliente por nro_sobre (API v1)
export async function buscarClientePorNroSobre(nro_sobre) {
  const response = await apiFetch(`${BACKEND_URL}/clientes/nro_sobre/?nro_sobre=${encodeURIComponent(nro_sobre)}`);
  if (!response.ok) {
    throw new Error("Error al buscar cliente por nro_sobre");
  }
  return await response.json();
}

// =============== UTILITARIAS ===============

// Validar datos de cliente
export function validarDatosCliente(clienteData) {
  const errores = [];
  
  if (!clienteData.nombre?.trim()) {
    errores.push("El nombre es requerido");
  }
  
  if (!clienteData.apellido?.trim()) {
    errores.push("El apellido es requerido");
  }
  
  if (!clienteData.telefono?.trim()) {
    errores.push("El teléfono es requerido");
  }
  
  return errores;
}

// Validar datos de anteojo
export function validarDatosAnteojo(anteojoData) {
  const errores = [];
  
  if (!anteojoData.precio_total || anteojoData.precio_total < 0) {
    errores.push("El precio total debe ser mayor a 0");
  }
  
  if (anteojoData.sena < 0) {
    errores.push("La seña no puede ser negativa");
  }
  
  if (anteojoData.sena > anteojoData.precio_total) {
    errores.push("La seña no puede ser mayor al precio total");
  }
  
  return errores;
}

// Calcular saldo automáticamente
export function calcularSaldo(precioTotal, sena) {
  const precio = parseFloat(precioTotal) || 0;
  const seña = parseFloat(sena) || 0;
  return Math.max(0, precio - seña);
}

// Estados válidos para anteojos
export const ESTADOS_ANTEOJO = [
  "Tomado",
  "Preparado",
  "En laboratorio",
  "Para retirar",
  "Entregado"
];

// Tipos de anteojos
export const TIPOS_ANTEOJO = [
  "General",
  "Lejos",
  "Cerca", 
  "Bifocal",
  "Progressive",
  "Multifocal",
  "Sol",
  "Trabajo"
];

// =============== FUNCIONES PARA ANTEOJOS POR ESTADO ===============

// Obtener anteojos por estado
export async function obtenerAnteojosPorEstado(estado) {
  const response = await apiFetch(`${API_V2_URL}/anteojos/estado/${encodeURIComponent(estado)}`);
  if (!response.ok) {
    throw new Error(`Error al obtener anteojos con estado ${estado}`);
  }
  return await response.json();
}

// =============== FUNCIONES PARA FINANZAS ===============

// Obtener datos financieros completos
export async function obtenerDatosFinancieros(filtros = {}) {
  const params = new URLSearchParams();
  if (filtros.fechaInicio) params.append('fecha_inicio', filtros.fechaInicio);
  if (filtros.fechaFin) params.append('fecha_fin', filtros.fechaFin);  
  if (filtros.mes) params.append('mes', filtros.mes);
  if (filtros.anio) params.append('anio', filtros.anio);
  if (filtros.limite) params.append('limite', filtros.limite);
  if (filtros.offset) params.append('offset', filtros.offset);
  
  const response = await apiFetch(`${API_V2_URL}/finanzas?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Error al obtener datos financieros");
  }
  return await response.json();
}

// Obtener resumen financiero
export async function obtenerResumenFinanciero() {
  const response = await apiFetch(`${API_V2_URL}/finanzas/resumen`);
  if (!response.ok) {
    throw new Error("Error al obtener resumen financiero");
  }
  return await response.json();
}

// Registrar egreso o gasto
export async function registrarEgresoFinanciero(egresoData) {
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(egresoData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al registrar egreso');
  }

  return await response.json();
}

// Obtener calendario de pagos pendientes
export async function obtenerCalendarioPagos(diasAdelante = 90) {
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos/calendario?dias_adelante=${diasAdelante}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener calendario de pagos');
  }

  return await response.json();
}

export async function marcarEgresoPagado(egresoId, payload) {
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos/${egresoId}/marcar-pagado`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al actualizar estado de pago');
  }

  return await response.json();
}

export async function obtenerHistorialPagos(params = {}) {
  const query = new URLSearchParams();
  if (params.solo_pagados !== undefined) query.set('solo_pagados', String(params.solo_pagados));
  if (params.fecha_inicio) query.set('fecha_inicio', params.fecha_inicio);
  if (params.fecha_fin) query.set('fecha_fin', params.fecha_fin);
  if (params.tipo) query.set('tipo', params.tipo);
  if (params.limite) query.set('limite', String(params.limite));

  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos/historial${suffix}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener historial de pagos');
  }

  return await response.json();
}

export async function obtenerBalance(params = {}) {
  const query = new URLSearchParams();
  query.set('tipo_periodo', params.tipo_periodo || 'mensual');
  if (params.fecha_inicio) query.set('fecha_inicio', params.fecha_inicio);
  if (params.fecha_fin) query.set('fecha_fin', params.fecha_fin);
  const response = await apiFetch(`${API_V2_URL}/finanzas/balance?${query.toString()}`, { method: 'GET' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener balance financiero');
  }
  return await response.json();
}

// =============== FUNCIONES PARA VENTAS ===============

export async function obtenerTiposVenta() {
  const response = await apiFetch(`${API_V2_URL}/ventas/tipos`);
  if (!response.ok) throw new Error('Error al obtener tipos de venta');
  return await response.json();
}

export async function crearTipoVenta(body) {
  const response = await apiFetch(`${API_V2_URL}/ventas/tipos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Error al crear tipo de venta');
  }
  return await response.json();
}

export async function crearVenta(ventaData) {
  const response = await apiFetch(`${API_V2_URL}/ventas/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ventaData),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Error al registrar venta');
  }
  return await response.json();
}

export async function obtenerVentas(filtros = {}) {
  const query = new URLSearchParams();
  if (filtros.tipo_venta_id) query.set('tipo_venta_id', filtros.tipo_venta_id);
  if (filtros.tipo_pago) query.set('tipo_pago', filtros.tipo_pago);
  if (filtros.desde) query.set('desde', filtros.desde);
  if (filtros.hasta) query.set('hasta', filtros.hasta);
  if (filtros.limite) query.set('limite', filtros.limite);
  if (filtros.cliente_id) query.set('cliente_id', filtros.cliente_id);
  const response = await apiFetch(`${API_V2_URL}/ventas/?${query.toString()}`);
  if (!response.ok) throw new Error('Error al obtener ventas');
  return await response.json();
}

export async function eliminarVenta(ventaId) {
  const response = await apiFetch(`${API_V2_URL}/ventas/${ventaId}`, { method: 'DELETE' });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Error al eliminar venta');
  }
  return await response.json();
}

export async function editarVenta(ventaId, ventaData) {
  const response = await apiFetch(`${API_V2_URL}/ventas/${ventaId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ventaData),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Error al editar venta');
  }
  return await response.json();
}

export async function obtenerEgresos(params = {}) {
  const query = new URLSearchParams();
  if (params.pagado !== undefined) query.set('pagado', String(params.pagado));
  if (params.tipo) query.set('tipo', params.tipo);
  if (params.fecha_inicio) query.set('fecha_inicio', params.fecha_inicio);
  if (params.fecha_fin) query.set('fecha_fin', params.fecha_fin);
  if (params.limite) query.set('limite', String(params.limite));

  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos${suffix}`, { method: 'GET' });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener egresos');
  }
  return await response.json();
}

export async function editarEgreso(egresoId, egresoData) {
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos/${egresoId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(egresoData),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al editar egreso');
  }
  return await response.json();
}

export async function eliminarEgreso(egresoId) {
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos/${egresoId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al eliminar egreso');
  }
  return await response.json();
}

export async function obtenerNotificacionesPagos(sinLeer = true, limite = 50) {
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos/notificaciones?sin_leer=${sinLeer}&limite=${limite}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener notificaciones de pagos');
  }

  return await response.json();
}

export async function marcarNotificacionPagoLeida(notificacionId) {
  const response = await apiFetch(`${API_V2_URL}/finanzas/egresos/notificaciones/${notificacionId}/leida`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al marcar notificación de pago');
  }

  return await response.json();
}

// Eliminar anteojo por ID
export async function eliminarAnteojo(anteojoId) {
  const response = await apiFetch(`${API_V2_URL}/anteojos/${anteojoId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar anteojo");
  }
  return await response.json();
}


// Obtener cristales por tipo
export async function obtenerCristalesPorTipo(tipoAnteojo) {
  const response = await apiFetch(`${API_V2_URL}/cristales/tipo/${encodeURIComponent(tipoAnteojo)}`);  
  if (!response.ok) {
    throw new Error(`Error al obtener cristales para ${tipoAnteojo}`);
  }
  return await response.json();
}

// Obtener todos los cristales
export async function obtenerCristales() {
  const response = await apiFetch(`${API_V2_URL}/cristales/`);
  if (!response.ok) {
    throw new Error('Error al obtener cristales');
  }
  return await response.json();
}

// Agregar nuevo cristal
export async function agregarCristal(cristalData) {
  const response = await apiFetch(`${API_V2_URL}/cristales/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(cristalData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al agregar cristal");
  }
  return await response.json();
}


// Editar cristal existente
export async function editarCristal(cristalId, cristalData) {
  const response = await apiFetch(`${API_V2_URL}/cristales/${cristalId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(cristalData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al editar cristal");
  }
  return await response.json();
}

// Eliminar cristal
export async function eliminarCristal(cristalId) {
  const response = await apiFetch(`${API_V2_URL}/cristales/${cristalId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar cristal");
  }
  return await response.json();
}

// =============== FUNCIONES PARA CATALOGOS NORMALIZADOS (CRISTALES) ===============

export async function obtenerCatalogosCristales(soloActivos = false) {
  const query = soloActivos ? '?solo_activos=true' : '';
  const response = await apiFetch(`${API_V2_URL}/catalogos/cristales/${query}`);
  if (!response.ok) {
    throw new Error('Error al obtener catálogos de cristales');
  }
  return await response.json();
}

export async function obtenerCatalogoCristalesPorTipo(tipo, soloActivos = false) {
  const query = soloActivos ? '?solo_activos=true' : '';
  const response = await apiFetch(`${API_V2_URL}/catalogos/cristales/${encodeURIComponent(tipo)}${query}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al obtener catálogo ${tipo}`);
  }
  return await response.json();
}

export async function crearCatalogoCristalesItem(tipo, nombre, direccion = null, telefonos = null, rango = null) {
  const payload = { nombre };
  if (direccion !== null) payload.direccion = direccion;
  if (telefonos !== null) payload.telefonos = telefonos;
  if (rango !== null) payload.rango = rango;

  const response = await apiFetch(`${API_V2_URL}/catalogos/cristales/${encodeURIComponent(tipo)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al crear item en ${tipo}`);
  }
  return await response.json();
}

export async function actualizarCatalogoCristalesItem(tipo, itemId, payload) {
  const response = await apiFetch(`${API_V2_URL}/catalogos/cristales/${encodeURIComponent(tipo)}/${itemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al actualizar item en ${tipo}`);
  }
  return await response.json();
}

export async function desactivarCatalogoCristalesItem(tipo, itemId) {
  const response = await apiFetch(`${API_V2_URL}/catalogos/cristales/${encodeURIComponent(tipo)}/${itemId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al desactivar item en ${tipo}`);
  }
  return await response.json();
}

export async function eliminarCatalogoCristalesItem(tipo, itemId) {
  const response = await apiFetch(`${API_V2_URL}/catalogos/cristales/${encodeURIComponent(tipo)}/${itemId}?eliminar_fisico=true`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al eliminar item en ${tipo}`);
  }
  return await response.json();
}

export async function detectarDuplicadosCatalogoCristales(tipo) {
  const response = await apiFetch(`${API_V2_URL}/catalogos/cristales/${encodeURIComponent(tipo)}/duplicados`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al detectar duplicados en ${tipo}`);
  }
  return await response.json();
}

// =============== FUNCIONES PARA ARMAZONES ===============

// Agregar nuevo armazón
export async function agregarArmazon(armazonData) {
  const response = await apiFetch(`${API_V2_URL}/armazones/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(armazonData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al agregar armazón");
  }
  return await response.json();
}

// Obtener todos los armazones
export async function obtenerArmazones() {
  const response = await apiFetch(`${API_V2_URL}/armazones/`);
  if (!response.ok) {
    throw new Error("Error al obtener armazones");
  }
  return await response.json();
}

// Obtener armazones disponibles (con stock > 0)
export async function obtenerArmazonesDisponibles() {
  const response = await apiFetch(`${API_V2_URL}/armazones/disponibles`);
  if (!response.ok) {
    throw new Error("Error al obtener armazones disponibles");
  }
  return await response.json();
}

// Obtener armazón por ID
export async function obtenerArmazonPorId(armazonId) {
  const response = await apiFetch(`${API_V2_URL}/armazones/${armazonId}`);
  if (!response.ok) {
    throw new Error("Error al obtener armazón");
  }
  return await response.json();
}

// Descontar stock de un armazón al venderlo
export async function usarArmazonStock(armazonId, cantidad = 1) {
  const response = await apiFetch(`${API_V2_URL}/armazones/${armazonId}/usar?cantidad=${cantidad}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al descontar stock de armazón");
  }
  return await response.json();
}

// Subir imagen de armazón (Google Drive API o local)
export async function subirImagenArmazon(file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiFetch(`${API_V2_URL}/armazones/upload-imagen`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al subir la imagen");
  }

  return await response.json();
}

// Editar armazón existente
export async function editarArmazon(armazonId, armazonData) {
  const response = await apiFetch(`${API_V2_URL}/armazones/${armazonId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(armazonData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al editar armazón");
  }
  return await response.json();
}

// Eliminar armazón
export async function eliminarArmazon(armazonId) {
  const response = await apiFetch(`${API_V2_URL}/armazones/${armazonId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar armazón");
  }
  return await response.json();
}

// =============== FUNCIONES PARA CATALOGOS DE ARMAZONES ===============

export async function obtenerCatalogosArmazones(soloActivos = false) {
  const query = soloActivos ? '?solo_activos=true' : '';
  const response = await apiFetch(`${API_V2_URL}/catalogos/armazones/${query}`);
  if (!response.ok) {
    throw new Error('Error al obtener catálogos de armazones');
  }
  return await response.json();
}

export async function obtenerCatalogoArmasonPorTipo(tipo, soloActivos = false) {
  const query = soloActivos ? '?solo_activos=true' : '';
  const response = await apiFetch(`${API_V2_URL}/catalogos/armazones/${encodeURIComponent(tipo)}${query}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al obtener catálogo ${tipo}`);
  }
  return await response.json();
}

export async function crearCatalogoArmazonItem(tipo, nombre) {
  const payload = { nombre };
  const response = await apiFetch(`${API_V2_URL}/catalogos/armazones/${encodeURIComponent(tipo)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al crear item en ${tipo}`);
  }
  return await response.json();
}

export async function actualizarCatalogoArmazonItem(tipo, itemId, payload) {
  const response = await apiFetch(`${API_V2_URL}/catalogos/armazones/${encodeURIComponent(tipo)}/${itemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al actualizar item en ${tipo}`);
  }
  return await response.json();
}

export async function desactivarCatalogoArmazonItem(tipo, itemId) {
  const response = await apiFetch(`${API_V2_URL}/catalogos/armazones/${encodeURIComponent(tipo)}/${itemId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al desactivar item en ${tipo}`);
  }
  return await response.json();
}

export async function eliminarCatalogoArmazonItem(tipo, itemId) {
  const response = await apiFetch(`${API_V2_URL}/catalogos/armazones/${encodeURIComponent(tipo)}/${itemId}?eliminar_fisico=true`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Error al eliminar item en ${tipo}`);
  }
  return await response.json();
}

// =============== FUNCIONES PARA LENTES DE CONTACTO ===============

// Agregar nuevo lente de contacto
export async function agregarLenteContacto(lenteData) {
  const response = await apiFetch(`${API_V2_URL}/lentes-contacto/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(lenteData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al agregar lente de contacto");
  }
  return await response.json();
}

// Obtener todos los lentes de contacto
export async function obtenerLentesContacto() {
  const response = await apiFetch(`${API_V2_URL}/lentes-contacto/`);
  if (!response.ok) {
    throw new Error("Error al obtener lentes de contacto");
  }
  return await response.json();
}

// Obtener lentes de contacto por tipo de visión
export async function obtenerLentesContactoPorTipo(tipoVision) {
  const response = await apiFetch(`${API_V2_URL}/lentes-contacto/tipo/${encodeURIComponent(tipoVision)}`);
  if (!response.ok) {
    throw new Error(`Error al obtener lentes de contacto para ${tipoVision}`);
  }
  return await response.json();
}

// Obtener solo lentes de contacto estéticas
export async function obtenerLentesContactoEsteticas() {
  const response = await apiFetch(`${API_V2_URL}/lentes-contacto/esteticas`);
  if (!response.ok) {
    throw new Error("Error al obtener lentes de contacto estéticas");
  }
  return await response.json();
}

// Editar lente de contacto existente
export async function editarLenteContacto(lenteId, lenteData) {
  const response = await apiFetch(`${API_V2_URL}/lentes-contacto/${lenteId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(lenteData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al editar lente de contacto");
  }
  return await response.json();
}

// Eliminar lente de contacto
export async function eliminarLenteContacto(lenteId) {
  const response = await apiFetch(`${API_V2_URL}/lentes-contacto/${lenteId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar lente de contacto");
  }
  return await response.json();
}

// =============== FUNCIONES PARA PRODUCTOS ===============

// Obtener todos los productos
export async function obtenerProductos() {
  const response = await apiFetch(`${API_V2_URL}/productos/`);
  if (!response.ok) {
    throw new Error("Error al obtener productos");
  }
  return await response.json();
}

// Agregar nuevo producto
export async function agregarProducto(productoData) {
  const response = await apiFetch(`${API_V2_URL}/productos/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(productoData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al agregar producto");
  }
  return await response.json();
}

// Editar producto existente
export async function editarProducto(productoId, productoData) {
  const response = await apiFetch(`${API_V2_URL}/productos/${productoId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(productoData),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al editar producto");
  }
  return await response.json();
}

// Eliminar producto
export async function eliminarProducto(productoId) {
  const response = await apiFetch(`${API_V2_URL}/productos/${productoId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar producto");
  }
  return await response.json();
}

// =============== FUNCIONES DE NOTIFICACIONES ===============

// Obtener alertas de laboratorio sin leer
export async function obtenerAlertasLaboratorio() {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/laboratorio`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener alertas de laboratorio");
  }
  return await response.json();
}

// Obtener todas las notificaciones
export async function obtenerNotificaciones(sinLeer = true, tipo = null) {
  let url = `${BACKEND_URL}/api/notificaciones/?sin_leer=${sinLeer}`;
  if (tipo) {
    url += `&tipo=${tipo}`;
  }
  const response = await apiFetch(url);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener notificaciones");
  }
  const data = await response.json();
  return data.notificaciones || data;
}

// Marcar notificación como leída
export async function marcarNotificacionLeida(notificacionId) {
  if (!notificacionId || notificacionId === 'undefined') return;
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/${notificacionId}/marcar-leida`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al marcar notificación como leída");
  }
  return await response.json();
}

// Marcar todas las notificaciones como leídas
export async function marcarTodasLeidasNotificaciones(tipo = null) {
  let url = `${BACKEND_URL}/api/notificaciones/marcar-todas-leidas`;
  if (tipo) {
    url += `?tipo=${tipo}`;
  }
  const response = await apiFetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al marcar notificaciones como leídas");
  }
  return await response.json();
}

// Eliminar notificación
export async function eliminarNotificacion(notificacionId) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/${notificacionId}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar notificación");
  }
  return await response.json();
}

// Obtener historial de notificaciones con filtros
export async function obtenerHistorialNotificaciones({ sinLeer = null, tipo = null, buscar = '', limite = 50, pagina = 1 } = {}) {
  let query = `?limite=${limite}&pagina=${pagina}`;
  if (sinLeer !== null && sinLeer !== undefined) {
    query += `&sin_leer=${sinLeer}`;
  }
  if (tipo) {
    query += `&tipo=${encodeURIComponent(tipo)}`;
  }
  if (buscar) {
    query += `&buscar=${encodeURIComponent(buscar)}`;
  }
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/historial${query}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener historial de notificaciones");
  }
  return await response.json();
}

// Eliminar notificaciones en lote por ID
export async function eliminarNotificacionesLote(ids = []) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/eliminar-lote`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar lote de notificaciones");
  }
  return await response.json();
}

// Eliminar notificaciones antiguas (anteriores a X días, ej: 30)
export async function eliminarNotificacionesAntiguas(dias = 30) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/eliminar-antiguas?dias=${dias}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al eliminar notificaciones antiguas");
  }
  return await response.json();
}

// =============== MENSAJERIA WHATSAPP ===============

export async function obtenerPreviewWhatsApp(anteojoId) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/whatsapp/preview/${anteojoId}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener preview de WhatsApp");
  }
  return await response.json();
}

export async function obtenerTemplateWhatsApp() {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/whatsapp/template`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener template de WhatsApp");
  }
  return await response.json();
}

export async function actualizarTemplateWhatsApp(template) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/whatsapp/template`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ template })
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al actualizar template de WhatsApp");
  }
  return await response.json();
}

export async function obtenerTemplateReview() {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/review/template`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener template de review");
  }
  return await response.json();
}

export async function actualizarTemplateReview(template) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/review/template`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ template })
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al actualizar template de review");
  }
  return await response.json();
}

export async function obtenerPreviewReview(anteojoId) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/review/preview/${anteojoId}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener preview de review");
  }
  return await response.json();
}

export async function registrarSolicitudReview(clienteId) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/review/registrar-solicitud/${clienteId}`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al registrar solicitud de review");
  }
  return await response.json();
}

export async function actualizarEstadoReviewCliente(clienteId, reviewRealizado) {
  const response = await apiFetch(`${BACKEND_URL}/api/v2/clientes/${clienteId}/review`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ review_realizado: reviewRealizado }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al actualizar estado de reseña del cliente");
  }
  return await response.json();
}

// =============== ESTADÍSTICAS DE VENTAS ===============

export async function obtenerEstadisticasVentas({ fechaDesde, fechaHasta } = {}) {
  const params = new URLSearchParams();
  if (fechaDesde) params.append('fecha_desde', fechaDesde);
  if (fechaHasta) params.append('fecha_hasta', fechaHasta);

  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await apiFetch(`${BACKEND_URL}/api/v2/estadisticas/ventas${query}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener estadísticas de ventas");
  }
  return await response.json();
}

// =============== CARGA DE ARMAZONES DESDE PDF ===============

export async function procesarPdfFactura(file, marca = '') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('marca', marca);

  const token = authService.obtenerToken();
  const headers = new Headers();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${BACKEND_URL}/api/armazones/procesar-pdf`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al procesar PDF");
  }

  return await response.json();
}

export async function cargarArmazonesDesdePdf(file, sol = false, idProveedor = null, marca = '') {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('sol', sol);
  formData.append('marca', marca);
  if (idProveedor) {
    formData.append('id_proveedor', idProveedor);
  }

  const token = authService.obtenerToken();
  const headers = new Headers();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${BACKEND_URL}/api/armazones/cargar-desde-pdf`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al cargar armazones desde PDF");
  }

  return await response.json();
}

// =============== GARANTÍAS ===============

export async function crearGarantia(datos) {
  const response = await fetch(`${API_V2_URL}/garantias`, {
    method: 'POST',
    headers: buildRequestHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(datos),
  });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    throw new Error(errorDetail || 'Error al crear garantía');
  }

  return await response.json();
}

export async function obtenerGarantías(anteojoId) {
  const response = await fetch(`${API_V2_URL}/garantias/anteojo/${anteojoId}`, {
    method: 'GET',
    headers: buildRequestHeaders(),
  });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    throw new Error(errorDetail || 'Error al obtener garantías');
  }

  return await response.json();
}

export async function obtenerGarantíasCliente(clienteId) {
  const response = await fetch(`${API_V2_URL}/garantias/cliente/${clienteId}`, {
    method: 'GET',
    headers: buildRequestHeaders(),
  });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    throw new Error(errorDetail || 'Error al obtener garantías del cliente');
  }

  return await response.json();
}

export async function obtenerResumenGarantías(clienteId) {
  const response = await fetch(`${API_V2_URL}/garantias/cliente/${clienteId}/en-proceso`, {
    method: 'GET',
    headers: buildRequestHeaders(),
  });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    throw new Error(errorDetail || 'Error al obtener resumen de garantías');
  }

  return await response.json();
}

export async function actualizarEstadoGarantia(garantiaId, datos) {
  const response = await fetch(`${API_V2_URL}/garantias/${garantiaId}`, {
    method: 'PATCH',
    headers: buildRequestHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(datos),
  });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    throw new Error(errorDetail || 'Error al actualizar estado de garantía');
  }

  return await response.json();
}

export async function enviarWhatsAppGarantia(garantiaId, datos) {
  const response = await fetch(`${API_V2_URL}/garantias/${garantiaId}/enviar-whatsapp`, {
    method: 'POST',
    headers: buildRequestHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(datos),
  });

  if (!response.ok) {
    const errorDetail = await parseErrorDetail(response);
    throw new Error(errorDetail || 'Error al enviar WhatsApp');
  }

  return await response.json();
}

export async function guardarArmazonesDesdeModal(armazones, sol = false, idProveedor = null, accesorios = []) {
  const token = authService.obtenerToken();
  const headers = new Headers();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  headers.set('Content-Type', 'application/json');

  const body = {
    armazones: armazones,
    accesorios: accesorios,
    sol: sol,
    id_proveedor: idProveedor
  };

  const response = await fetch(`${BACKEND_URL}/api/armazones/guardar-desde-modal`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al guardar armazones desde modal");
  }

  return await response.json();
}

// =============== DATOS DE MI ÓPTICA ===============

export async function obtenerMiOptica() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica`);
  if (!response.ok) return null;
  return await response.json();
}

// =============== CONFIG DE IMPRESIÓN DE SOBRE ===============

export async function obtenerPrintConfig() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/print-config`);
  if (!response.ok) return null;
  return await response.json();
}

export async function guardarPrintConfig(config) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/print-config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(config),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Error al guardar config de impresión");
  }
  return await response.json();
}

// =============== DESCUENTO EFECTIVO POR ÓPTICA ===============

export async function obtenerDescuentoEfectivo() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/descuento-efectivo`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener descuento efectivo');
  }
  return await response.json();
}

export async function guardarDescuentoEfectivo(porcentaje) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/descuento-efectivo`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ descuento_efectivo: porcentaje }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Error al guardar descuento efectivo");
  }
  return await response.json();
}

export async function obtenerDescuentoArmazones() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/descuento-armazones`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener descuento de armazones');
  }
  return await response.json();
}

export async function guardarDescuentoArmazones(porcentaje) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/descuento-armazones`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ descuento_armazones: porcentaje }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Error al guardar descuento de armazones");
  }
  return await response.json();
}

// =============== POLÍTICA COMERCIAL Y DESCUENTOS POR ÓPTICA ===============

export async function obtenerPoliticaDescuentos() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/politica-descuentos`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener política de descuentos');
  }
  return await response.json();
}

export async function guardarPoliticaDescuentos(politica) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/politica-descuentos`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(politica),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Error al guardar política de descuentos");
  }
  return await response.json();
}

export async function obtenerCuotasOptica() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/cuotas`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener cuotas de la óptica');
  }
  return await response.json();
}

export async function guardarCuotasOptica(cuotasDisponibles, maxCuotas = null) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/cuotas`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      cuotas_disponibles: cuotasDisponibles,
      cuotas_sin_interes: maxCuotas,
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Error al guardar cuotas de la óptica");
  }
  return await response.json();
}

// =============== CONFIGURACIÓN MERCADO PAGO POR ÓPTICA ===============

export async function obtenerConfiguracionMercadoPago() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/mercadopago`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener configuración de Mercado Pago');
  }
  return await response.json();
}

export async function guardarConfiguracionMercadoPago(payload) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/mercadopago`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Error al guardar configuración de Mercado Pago");
  }
  return await response.json();
}

export async function testearConexionMercadoPago(payload = {}) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/mercadopago/test-connection`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Error al verificar conexión con Mercado Pago");
  }
  return await response.json();
}

export async function obtenerHorariosWhatsApp() {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/whatsapp/horarios`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al obtener horarios de WhatsApp");
  }
  return await response.json();
}

export async function actualizarHorariosWhatsApp(horarioSemana, horarioSabado) {
  const response = await apiFetch(`${BACKEND_URL}/api/notificaciones/whatsapp/horarios`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      horario_semana: horarioSemana,
      horario_sabado: horarioSabado
    })
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Error al actualizar horarios de WhatsApp");
  }
  return await response.json();
}

// =============== FACTURACION AFIP ===============

export async function crearFactura(payload) {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/crear`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al crear la factura');
  }

  return await response.json();
}

export async function listarFacturas(params = {}) {
  const query = new URLSearchParams();

  if (params.tipo_factura) query.set('tipo_factura', params.tipo_factura);
  if (params.estado) query.set('estado', params.estado);
  if (params.page) query.set('page', String(params.page));
  if (params.page_size) query.set('page_size', String(params.page_size));

  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/${suffix}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al listar facturas');
  }

  return await response.json();
}

export async function obtenerEstadisticasFacturacion() {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/estadisticas/`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener estadísticas de facturación');
  }

  return await response.json();
}

export async function autorizarFacturaAFIP(facturaId) {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/autorizar/${facturaId}`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al autorizar factura en AFIP');
  }

  return await response.json();
}

export async function emitirNotaCredito(payload) {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/nota-credito`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al emitir la Nota de Crédito');
  }

  return await response.json();
}

export async function obtenerAfipConfig() {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/afip-config`, {
    method: 'GET',
  });

  if (!response.ok) {
    return null;
  }

  return await response.json();
}

export async function obtenerEstadoAsistenteAfip() {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/asistente/estado`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener estado del asistente AFIP');
  }

  return await response.json();
}

export async function generarSolicitudAfip(datos) {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/asistente/generar-solicitud`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al generar la solicitud de certificado AFIP');
  }

  return await response.json();
}

export async function subirCertificadoAfip(archivo) {
  const formData = new FormData();
  formData.append('file', archivo);

  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/asistente/subir-certificado`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al validar y subir el certificado AFIP');
  }

  return await response.json();
}

export async function probarConexionAfip() {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/asistente/probar-conexion`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error en la prueba de conexión con AFIP');
  }

  return await response.json();
}

export async function descargarFacturaPDF(facturaId, numeroComprobante, tipoFactura = '') {
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/descargar-pdf/${facturaId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al descargar PDF de comprobante');
  }

  const esNC = String(tipoFactura).toUpperCase().startsWith('NC');
  const tipoLabel = esNC ? 'Nota de Credito' : 'Factura';

  const blob = await response.blob();
  const fileURL = globalThis.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = fileURL;
  anchor.download = numeroComprobante
    ? `${tipoLabel} H&L Optica nº${numeroComprobante}.pdf`
    : `${tipoLabel} H&L Optica nº${facturaId}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  globalThis.URL.revokeObjectURL(fileURL);
}

export async function listarFacturasCliente(clienteId) {
  if (!clienteId) return { facturas: [] };
  const response = await apiFetch(`${BACKEND_URL}/api/facturacion/cliente/${clienteId}`, {
    method: 'GET',
  });
  if (!response.ok) {
    return { facturas: [] };
  }
  return await response.json();
}

// ===== FUNCIONES PARA PRESUPUESTOS Y DESCRIPCIONES DE CRISTALES =====


export async function obtenerDescripcionesCristales(tipoVision = '', marca = '', tipoProducto = '') {
  const query = new URLSearchParams();
  if (tipoVision) query.append('tipo_vision', tipoVision);
  if (marca) query.append('marca', marca);
  if (tipoProducto) query.append('tipo_producto', tipoProducto);

  const url = `${API_V2_URL}/presupuestos/descripciones${query.toString() ? `?${query.toString()}` : ''}`;
  const response = await apiFetch(url, { method: 'GET' });
  if (!response.ok) {
    const errorMsg = await parseErrorDetail(response);
    throw new Error(errorMsg || 'Error al obtener descripciones de cristales');
    throw new Error(errorMsg || 'Error al obtener descripciones');
  }
  return await response.json();
}

export async function crearDescripcionCristal(datos) {
  const response = await apiFetch(`${API_V2_URL}/presupuestos/descripciones`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  });
  if (!response.ok) {
    const errorMsg = await parseErrorDetail(response);
    throw new Error(errorMsg || 'Error al crear descripción de cristal');
    throw new Error(errorMsg || 'Error al crear descripción');
  }
  return await response.json();
}

export async function actualizarDescripcionCristal(id, datos) {
  const response = await apiFetch(`${API_V2_URL}/presupuestos/descripciones/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  });
  if (!response.ok) {
    const errorMsg = await parseErrorDetail(response);
    throw new Error(errorMsg || 'Error al actualizar descripción de cristal');
    throw new Error(errorMsg || 'Error al actualizar descripción');
  }
  return await response.json();
}

export async function eliminarDescripcionCristal(id) {
  const response = await apiFetch(`${API_V2_URL}/presupuestos/descripciones/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorMsg = await parseErrorDetail(response);
    throw new Error(errorMsg || 'Error al eliminar descripción de cristal');
    throw new Error(errorMsg || 'Error al eliminar descripción');
  }
  return await response.json();
}

export async function asignarCaracteristicaProductos(datos) {
  const response = await apiFetch(`${API_V2_URL}/presupuestos/asignar-caracteristica`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  });
  if (!response.ok) {
    const errorMsg = await parseErrorDetail(response);
    throw new Error(errorMsg || 'Error al asignar característica a productos');
  }
  return await response.json();
}

export async function enviarPresupuestoWhatsApp(datos) {
  const response = await apiFetch(`${API_V2_URL}/presupuestos/enviar-whatsapp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  });
  if (!response.ok) {
    const errorMsg = await parseErrorDetail(response);
    throw new Error(errorMsg || 'Error al enviar presupuesto por WhatsApp');
  }
  return await response.json();
}

export async function activarUsuario(usuarioId, opticaId = null) {
  let response = await apiFetch(`${BACKEND_URL}/api/auth/usuarios/${usuarioId}/activar`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok && opticaId) {
    response = await apiFetch(`${BACKEND_URL}/api/superadmin/opticas/${opticaId}/usuarios/${usuarioId}/activar`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
    });
  }
  if (!response.ok) {
    const errorMsg = await parseErrorDetail(response);
    throw new Error(errorMsg || "Error al activar usuario");
  }
  return await response.json();
}


// =============== LOGÍSTICA Y ENVÍOS (ANDREANI + ENVIA.COM) ===============

export async function obtenerConfiguracionLogistica() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/logistica`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener configuración de logística');
  }
  return await response.json();
}

export async function guardarConfiguracionLogistica(payload) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/logistica`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al guardar configuración de logística');
  }
  return await response.json();
}

export async function probarCotizacionLogistica(payload) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/logistica/test-cotizacion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al simular cotización de envío');
  }
  return await response.json();
}

export async function obtenerEnviosWeb() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/envios-web`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener los envíos web');
  }
  return await response.json();
}

export async function actualizarEstadoEnvioWeb(pedidoId, payload) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/envios-web/${pedidoId}/estado`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al actualizar estado del envío');
  }
  return await response.json();
}

export async function generarEtiquetaEnvioWeb(pedidoId, payload = {}) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/envios-web/${pedidoId}/generar-etiqueta`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al generar etiqueta y código de seguimiento');
  }
  return await response.json();
}


// =============== PERSONALIZACIÓN VISUAL Y LINK DE TIENDA ONLINE ===============

export async function obtenerConfiguracionTienda() {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/tienda-config`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al obtener configuración de la tienda online');
  }
  return await response.json();
}

export async function guardarConfiguracionTienda(payload) {
  const response = await apiFetch(`${BACKEND_URL}/api/auth/mi-optica/tienda-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Error al guardar configuración de la tienda online');
  }
  return await response.json();
}
